import type { LandingPageUrls } from '../../landing-page-editor/landing-page-editor';

export type StudioDeviceId = 'large-desktop' | 'desktop' | 'tablet' | 'phone';

export type StudioDevice = { id: StudioDeviceId; label: string; key: string };

/**
 * Canvas artboards → PREFERRED dimension key (preview-iframe contract, rule
 * 2). The keys actually packaged on a version are the source of truth: the
 * backend writes three per version (`400x800`, `1200x800`, `1400x900`),
 * versions analyzed before 2026-09-11 carry six (`600x800`, `800x800`,
 * `1000x800` as well). A device resolves to its preferred key when present,
 * else to the packaged key nearest in width (see selectDevicePage) — never
 * to a key the version does not have. The artboard then lays its iframe out
 * at the RESOLVED key's width (rule 3): a capture only lays out correctly at
 * the width it was rendered at. The single view is the exception: it fills
 * the panel and switches keys at the width breakpoints. Widest first: the
 * canvas orders its artboards this way.
 */
export const STUDIO_DEVICES: readonly StudioDevice[] = [
  { id: 'large-desktop', label: 'Large desktop', key: '1400x900' },
  { id: 'tablet', label: 'Tablet', key: '800x800' },
  { id: 'phone', label: 'Phone', key: '400x800' },
];

export type DevicePage = {
  key: string;
  /** The key's viewport width in CSS px. */
  width: number;
  /** The key's viewport height in CSS px — what `vh` should resolve against. */
  height: number;
  url: string;
};

/** `<width>x<height>` → both dimensions, or null for a malformed key. */
export function parseDimensionKey(key: string): { width: number; height: number } | null {
  const [width, height] = key.split('x').map((part) => parseInt(part, 10));
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  return { width, height };
}

/**
 * The device's packaged page: its preferred key when the version has it,
 * else the packaged key nearest in width, a tie going to the wider one (a
 * capture rendered wider than the panel scales down; one rendered narrower
 * would show a phone layout on a desktop toggle). Null while nothing is
 * packaged yet. Malformed keys are ignored.
 */
export function selectDevicePage(
  urls: LandingPageUrls | null | undefined,
  deviceId: StudioDeviceId,
): DevicePage | null {
  if (!urls) return null;
  const device = STUDIO_DEVICES.find((d) => d.id === deviceId);
  if (!device) return null;
  const preferred = parseDimensionKey(device.key) as { width: number; height: number };
  if (urls[device.key]) {
    return { key: device.key, ...preferred, url: urls[device.key] };
  }

  let best: DevicePage | null = null;
  for (const [key, url] of Object.entries(urls)) {
    const dimension = parseDimensionKey(key);
    if (!dimension) continue;
    const candidate = { key, ...dimension, url };
    if (!best) {
      best = candidate;
      continue;
    }
    const distance = Math.abs(dimension.width - preferred.width);
    const bestDistance = Math.abs(best.width - preferred.width);
    if (distance < bestDistance || (distance === bestDistance && dimension.width > best.width)) {
      best = candidate;
    }
  }
  return best;
}
